import Axios, { CancelTokenSource } from "axios";
import { useCallback, useMemo, useRef } from "react";
import { FetchCallBackResult, FetchFn, InitialState, useDataFetch } from "../../../../core";
import { ResponseStatus } from "../../explore";
import { UseCaseFeedResponseList } from "../types";
import { useCaseApiService } from "../UseCaseApiService";

type Result = UseCaseFeedResponseList;

export const useFetchFeedForUseCase = (
  useCaseId: string,
  lazy = false,
  feedListType: "TIMELINE" | "SUMMARY" | "DATASTORY" | "INSIGHTS" | "DASHBOARD" = "TIMELINE"
) => {
  const initialState = useMemo<InitialState<Result, string>>(
    () => ({
      data: null,
      error: "",
      isError: false,
      isFetching: !lazy,
      isSuccess: false
    }),
    [lazy]
  );

  const cancelTokenRef = useRef<CancelTokenSource>();

  const fetchFn = useCallback<FetchFn<Result, string>>(async () => {
    cancelTokenRef.current?.cancel();
    cancelTokenRef.current = Axios.CancelToken.source();

    const result: FetchCallBackResult<Result, string> = {
      data: null,
      error: "",
      isError: false,
      isSuccess: false
    };

    try {
      const errors: string[] = [];

      const { data, error, message, cancelled } = await useCaseApiService.getFeedListForUseCase(
        useCaseId,
        cancelTokenRef.current.token,
        feedListType
      );

      if (!cancelled) {
        if (error) {
          result.error = message;
          result.isError = true;
        } else {
          const { responseInfo, feedResponseList } = data || {};

          if (responseInfo?.status === ResponseStatus.FAILED || !feedResponseList) {
            errors.push(responseInfo.errors?.join(", ") || `Error fetching feed for Copilot ${useCaseId}`);
          } else {
            result.data = data;
          }

          result.isError = errors.length > 0;
          result.isSuccess = errors.length === 0;
          result.error = errors.join(", ");
        }
      } else {
        result.isCancelled = true;
      }
    } catch (err) {
      result.isError = true;
      result.error = err.message || String(err);
    }

    return result;
  }, [feedListType, useCaseId]);

  return useDataFetch(fetchFn, initialState, lazy);
};
